import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaUser, FaHeartbeat, FaFileAlt, FaImages, FaVideo, FaLink, FaEdit, FaPhone, FaEnvelope, FaMapMarkerAlt, FaAppleAlt,
} from 'react-icons/fa';
import RightWrapper from '../../../Components/CommonComponents/ComponentWrapper';
import CustomBreadcrumbs from '../../../Components/CommonComponents/CustomBreadcrumbs';
import CustomSpinner from '../../../Components/CommonComponents/CustomSpinner';
import VitalsTab from './VitalsTab';
import DietTab from './DietTab';
import FilesTab from './FilesTab';
import ConsultationsTab from './ConsultationsTab';
import PortalLinkTab from './PortalLinkTab';
import { fetchPatientOverview } from '../../../Services/patientProfile';

const fullName = (p) =>
  [p?.firstName, p?.middleName, p?.surname].filter(Boolean).join(' ').trim() || 'Patient';

const initials = (p) =>
  `${p?.firstName?.[0] || ''}${p?.surname?.[0] || ''}`.toUpperCase() || 'P';

const age = (dob) => {
  if (!dob) return null;
  const born = new Date(dob);
  if (Number.isNaN(born.getTime())) return null;
  const diff = Date.now() - born.getTime();
  return Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
};

const StatTile = ({ icon, label, value }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3 text-center shadow-sm">
    <div className="mx-auto flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
      {icon}
    </div>
    <p className="mt-2 text-xl font-bold text-slate-800">{value}</p>
    <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
  </div>
);

const Detail = ({ label, value }) => (
  <div>
    <dt className="text-[11px] uppercase tracking-wide text-slate-500">{label}</dt>
    <dd className="mt-0.5 text-sm text-slate-800">{value || '—'}</dd>
  </div>
);

const TABS = [
  { id: 'overview', label: 'Overview', icon: <FaUser /> },
  { id: 'vitals', label: 'Vitals', icon: <FaHeartbeat /> },
  { id: 'diet', label: 'Diet', icon: <FaAppleAlt /> },
  { id: 'documents', label: 'Documents', icon: <FaFileAlt /> },
  { id: 'pictures', label: 'Pictures', icon: <FaImages /> },
  { id: 'consultations', label: 'Video consults', icon: <FaVideo /> },
  { id: 'link', label: 'Patient link', icon: <FaLink /> },
];

export default function PatientProfile() {
  const { patientId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'overview';

  const [overview, setOverview] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setOverview(await fetchPatientOverview(patientId));
      setError(null);
    } catch (err) {
      const message = err?.response?.data?.error || 'Could not load this patient profile';
      setError(message);
      toast.error(message);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  const setTab = (id) => setSearchParams({ tab: id }, { replace: true });

  if (error) {
    return (
      <RightWrapper>
        <div className="p-6">
          <p className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-center text-rose-700">{error}</p>
        </div>
      </RightWrapper>
    );
  }

  if (!overview) {
    return (
      <RightWrapper>
        <CustomSpinner />
      </RightWrapper>
    );
  }

  const { patient, counts, consultations, recentVitals, portalAccess } = overview;
  const patientAge = age(patient?.dateOfBirth);

  return (
    <RightWrapper>
      <div className="p-3 md:p-6">
        <CustomBreadcrumbs
          data={[
            { title: 'Dashboard', url: '/dashboard' },
            { title: 'Patients', url: '/patients' },
            { title: fullName(patient) },
          ]}
        />

        {/* --------------------------------- header -------------------------------- */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-start gap-4">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary-500 text-xl font-bold text-white">
              {initials(patient)}
            </div>

            <div className="min-w-0 flex-1">
              <h1 className="text-2xl font-bold text-slate-800">{fullName(patient)}</h1>
              <p className="mt-0.5 text-sm text-slate-500">
                {patient?.patientId && <span className="font-medium text-slate-600">{patient.patientId}</span>}
                {patient?.gender && ` · ${patient.gender}`}
                {patientAge !== null && ` · ${patientAge} yrs`}
                {patient?.bloodGroup && ` · ${patient.bloodGroup}`}
              </p>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                {patient?.mobile && (
                  <span className="inline-flex items-center gap-1.5">
                    <FaPhone className="text-slate-400" /> {patient.mobile}
                  </span>
                )}
                {patient?.email && (
                  <span className="inline-flex items-center gap-1.5">
                    <FaEnvelope className="text-slate-400" /> {patient.email}
                  </span>
                )}
                {(patient?.city || patient?.state) && (
                  <span className="inline-flex items-center gap-1.5">
                    <FaMapMarkerAlt className="text-slate-400" />
                    {[patient.city, patient.state].filter(Boolean).join(', ')}
                  </span>
                )}
              </div>
            </div>

            <div className="flex gap-2">
              {portalAccess && (
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700">
                  <FaLink /> Patient link active
                </span>
              )}
              <Link
                to={`/patients/${patientId}`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <FaEdit /> Edit details
              </Link>
            </div>
          </div>
        </div>

        {/* ---------------------------------- tabs --------------------------------- */}
        <div className="mt-5 flex gap-1 overflow-x-auto border-b border-slate-200">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition ${
                tab === t.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        <div className="mt-5">
          {tab === 'overview' && (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-3 md:grid-cols-6">
                <StatTile icon={<FaFileAlt />} label="Documents" value={counts.documents} />
                <StatTile icon={<FaImages />} label="Pictures" value={counts.pictures} />
                <StatTile icon={<FaVideo />} label="Consults" value={counts.consultations} />
                <StatTile icon={<FaHeartbeat />} label="Vital forms" value={counts.vitalTemplates} />
                <StatTile icon={<FaUser />} label="Appointments" value={counts.appointments} />
                <StatTile icon={<FaFileAlt />} label="Prescriptions" value={counts.prescriptions} />
              </div>

              <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
                <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
                  <h3 className="font-semibold text-slate-800">Patient details</h3>
                  <dl className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4">
                    <Detail label="Date of birth" value={patient?.dateOfBirth} />
                    <Detail label="Blood group" value={patient?.bloodGroup} />
                    <Detail label="Weight" value={patient?.weight && `${patient.weight} kg`} />
                    <Detail label="Height" value={patient?.height && `${patient.height} cm`} />
                    <Detail label="Marital status" value={patient?.maritalStatus} />
                    <Detail label="Profession" value={patient?.profession} />
                    <Detail label="Education" value={patient?.education} />
                    <Detail label="Mother tongue" value={patient?.motherTongue} />
                    <Detail label="Religion" value={patient?.religion} />
                    <Detail label="Primary diagnosis" value={patient?.diagnosis?.primary} />
                    <Detail label="Secondary diagnosis" value={patient?.diagnosis?.secondary} />
                    <Detail label="Tertiary diagnosis" value={patient?.diagnosis?.tertiary} />
                    <Detail label="Patient status" value={patient?.status_by_visit || patient?.PatientStatus || patient?.patientStatus} />
                  </dl>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <h3 className="font-semibold text-slate-800">Latest vitals</h3>
                  {!recentVitals?.length ? (
                    <p className="mt-3 text-sm text-slate-500">
                      No readings yet.{' '}
                      <button onClick={() => setTab('vitals')} className="font-medium text-primary-600 hover:underline">
                        Set up vitals tracking
                      </button>
                    </p>
                  ) : (
                    <ul className="mt-3 divide-y divide-slate-100">
                      {recentVitals.slice(0, 5).map((r) => (
                        <li key={r._id} className="py-2">
                          <div className="flex items-baseline justify-between gap-2">
                            <p className="truncate text-sm font-medium text-slate-800">{r.templateName}</p>
                            <span className="shrink-0 text-[11px] text-slate-500">
                              {new Date(r.recordedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                            </span>
                          </div>
                          <p className="mt-0.5 truncate text-xs text-slate-500">
                            {Object.entries(r.values || {})
                              .slice(0, 4)
                              .map(([k, v]) => `${k}: ${v}`)
                              .join(' · ')}
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                  <button
                    onClick={() => setTab('vitals')}
                    className="mt-3 text-xs font-medium text-primary-600 hover:underline"
                  >
                    View all vitals and graphs →
                  </button>
                </div>
              </div>
            </div>
          )}

          {tab === 'vitals' && <VitalsTab patientId={patientId} />}
          {tab === 'diet' && <DietTab patientId={patientId} />}
          {tab === 'documents' && <FilesTab patientId={patientId} mode="documents" onChanged={load} />}
          {tab === 'pictures' && <FilesTab patientId={patientId} mode="pictures" onChanged={load} />}
          {tab === 'consultations' && <ConsultationsTab consultations={consultations} />}
          {tab === 'link' && <PortalLinkTab patientId={patientId} patient={patient} onChanged={load} />}
        </div>
      </div>
    </RightWrapper>
  );
}
